import React from 'react';
import { Euro } from 'lucide-react';
import { Input } from '../../ui/input';
import { FINANCING_TYPES, DEFAULT_CONDITIONS, formatPrice } from './constants';

function OfferAmountStep({ form, setForm, selectedMandate }) {
    const toggleCondition = (condition) => {
        setForm((f) => {
            const conditions = f.conditions.includes(condition)
                ? f.conditions.filter((c) => c !== condition)
                : [...f.conditions, condition];
            return { ...f, conditions };
        });
    };

    return (
        <div className="space-y-5">
            <div>
                <label className="text-xs font-semibold text-white/60 mb-2 block">Montant de l'offre *</label>
                {selectedMandate && (
                    <p className="text-[10px] text-white/30 mb-2">
                        Prix affiché : {formatPrice(selectedMandate.price)} €
                    </p>
                )}
                <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FF914D] w-4 h-4" />
                    <Input
                        type="number"
                        value={form.offer_amount}
                        onChange={(e) => setForm((f) => ({ ...f, offer_amount: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white text-lg font-bold h-12 pl-10"
                        placeholder="350 000"
                        data-testid="offer-amount-input"
                    />
                </div>
                {selectedMandate && form.offer_amount && (
                    <p
                        className={`text-xs mt-1 ${
                            parseFloat(form.offer_amount) < selectedMandate.price
                                ? 'text-[#FF914D]'
                                : 'text-green-400'
                        }`}
                    >
                        {parseFloat(form.offer_amount) < selectedMandate.price
                            ? `${((1 - parseFloat(form.offer_amount) / selectedMandate.price) * 100).toFixed(1)}% en dessous du prix affiché`
                            : 'Au prix ou au dessus'}
                    </p>
                )}
            </div>

            <div>
                <label className="text-xs font-semibold text-white/60 mb-2 block">Type de financement</label>
                <div className="grid grid-cols-3 gap-2">
                    {FINANCING_TYPES.map((ft) => (
                        <button
                            key={ft.value}
                            onClick={() =>
                                setForm((f) => ({ ...f, financing: { ...f.financing, type: ft.value } }))
                            }
                            className={`p-3 rounded-xl border text-center transition-all ${
                                form.financing.type === ft.value
                                    ? 'border-[#FF914D]/50 bg-[#FF914D]/10 text-white'
                                    : 'border-white/5 bg-white/[0.02] text-white/40 hover:text-white/60'
                            }`}
                            data-testid={`financing-${ft.value}`}
                        >
                            <ft.icon className="w-5 h-5 mx-auto mb-1" />
                            <p className="text-xs font-medium">{ft.label}</p>
                        </button>
                    ))}
                </div>
            </div>

            {form.financing.type !== 'cash' && (
                <div className="space-y-3 p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] text-white/40 mb-1 block">Apport personnel (€)</label>
                            <Input
                                type="number"
                                value={form.financing.personal_contribution}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        financing: { ...f.financing, personal_contribution: e.target.value },
                                    }))
                                }
                                className="bg-white/5 border-white/10 text-white text-sm h-10"
                                placeholder="50 000"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-white/40 mb-1 block">Montant du prêt (€)</label>
                            <Input
                                type="number"
                                value={form.financing.loan_amount}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        financing: { ...f.financing, loan_amount: e.target.value },
                                    }))
                                }
                                className="bg-white/5 border-white/10 text-white text-sm h-10"
                                placeholder="300 000"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] text-white/40 mb-1 block">Durée du prêt (mois)</label>
                            <Input
                                type="number"
                                value={form.financing.loan_duration_months}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        financing: {
                                            ...f.financing,
                                            loan_duration_months: parseInt(e.target.value, 10) || 0,
                                        },
                                    }))
                                }
                                className="bg-white/5 border-white/10 text-white text-sm h-10"
                                placeholder="240"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-white/40 mb-1 block">Banque</label>
                            <Input
                                value={form.financing.bank_name}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        financing: { ...f.financing, bank_name: e.target.value },
                                    }))
                                }
                                className="bg-white/5 border-white/10 text-white text-sm h-10"
                                placeholder="Crédit Agricole"
                            />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.financing.has_pre_approval}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    financing: { ...f.financing, has_pre_approval: e.target.checked },
                                }))
                            }
                            className="rounded border-white/20 bg-white/5 text-[#FF914D]"
                        />
                        <span className="text-xs text-white/60">Accord de principe bancaire obtenu</span>
                    </label>
                </div>
            )}

            <div>
                <label className="text-xs font-semibold text-white/60 mb-2 block">Validité de l'offre (jours)</label>
                <Input
                    type="number"
                    value={form.validity_days}
                    onChange={(e) =>
                        setForm((f) => ({ ...f, validity_days: parseInt(e.target.value, 10) || 10 }))
                    }
                    className="bg-white/5 border-white/10 text-white text-sm h-10 w-24"
                />
            </div>

            <div>
                <label className="text-xs font-semibold text-white/60 mb-2 block">Conditions suspensives</label>
                <div className="space-y-1.5">
                    {DEFAULT_CONDITIONS.map((c) => (
                        <label
                            key={c}
                            className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-white/5 transition-colors"
                        >
                            <input
                                type="checkbox"
                                checked={form.conditions.includes(c)}
                                onChange={() => toggleCondition(c)}
                                className="rounded border-white/20 bg-white/5 text-[#FF914D] mt-0.5"
                            />
                            <span className="text-xs text-white/60">{c}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <label className="text-xs font-semibold text-white/60 mb-2 block">Message personnel (optionnel)</label>
                <textarea
                    value={form.personal_message}
                    onChange={(e) => setForm((f) => ({ ...f, personal_message: e.target.value }))}
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3 placeholder:text-white/30 focus:outline-none focus:border-[#681793]/50 resize-none"
                    placeholder="Motiver votre offre..."
                    data-testid="personal-message-input"
                />
            </div>
        </div>
    );
}

export default OfferAmountStep;
